import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Layout } from './components/Layout';
import { PublicLayout } from './components/PublicLayout';
import { PublicAppLayout } from './components/PublicAppLayout';
import { ProtectedRoute } from './routes/ProtectedRoute';
import { RoleRoute } from './routes/RoleRoute';
import { LevelsRoute } from './routes/LevelsRoute';
import { CvRoute } from './routes/CvRoute';
import Home from './pages/Home';
import CatalogLevels from './pages/CatalogLevels';
import CertificationDetail from './pages/CertificationDetail';
import TeamMemberDashboard from './pages/TeamMemberDashboard';
import TeamMemberCompetenceDetail from './pages/TeamMemberCompetenceDetail';
import TeamMemberCertificationDetail from './pages/TeamMemberCertificationDetail';
import AssessmentWizard from './pages/AssessmentWizard';
import CompetenceDetail from './pages/CompetenceDetail';
import SkillList from './pages/SkillList';
import SkillAssessmentWizard from './pages/SkillAssessmentWizard';
import SkillDetail from './pages/SkillDetail';
import CompetenceLevelDetail from './pages/CompetenceLevelDetail';
import ReviewPanel from './pages/ReviewPanel';
import DepartmentAdmin from './pages/DepartmentAdmin';
import CompetenceAdmin from './pages/CompetenceAdmin';
import JobAdmin from './pages/JobAdmin';
import JobPositioning from './pages/JobPositioning';
import JobDetail from './pages/JobDetail';
import SkillAdmin from './pages/SkillAdmin';
import UserAdmin from './pages/UserAdmin';
import CertificationAdmin from './pages/CertificationAdmin';
import MyCertifications from './pages/MyCertifications';
import MyCertificationDetail from './pages/MyCertificationDetail';
import Profile from './pages/Profile';
import MyCv from './pages/MyCv';
import ExternalCvList from './pages/ExternalCvList';
import ExternalCvDetail from './pages/ExternalCvDetail';
import ExternalCvCompetenceDetail from './pages/ExternalCvCompetenceDetail';
import ExternalCvJobDetail from './pages/ExternalCvJobDetail';
import ExternalCvCertificationDetail from './pages/ExternalCvCertificationDetail';
import ExploreList from './pages/ExploreList';
import ExploreDetail from './pages/ExploreDetail';
import AdminScoringSettings from './pages/AdminScoringSettings';
import Login from './pages/Login';
import Callback from './pages/Callback';
import Changelog from './pages/Changelog';
import PublicCvUpload from './pages/public/PublicCvUpload';
import PublicCvRecap from './pages/public/PublicCvRecap';
import PublicSkillAssessment from './pages/public/PublicSkillAssessment';
import PublicCvCompetenceDetail from './pages/public/PublicCvCompetenceDetail';
import PublicCvJobDetail from './pages/public/PublicCvJobDetail';
import PublicCvCertificationDetail from './pages/public/PublicCvCertificationDetail';

/**
 * Configurazione delle rotte dell'applicazione con le guardie per ruolo.
 *
 * Le rotte sono organizzate in tre macro-sezioni, ciascuna con il proprio
 * prefisso di path (vedi la navigazione a sezioni, `Navbar`):
 *  - **Assessment** (`/assessment/*`): analisi personale di skill, competenze,
 *    professioni e certificazioni; richiede solo la sessione (`ProtectedRoute`).
 *    La radice `/` è la home (hub) da cui si scelgono le sezioni.
 *  - **Review** (`/review`): pannello per reviewer e manager
 *    (`RoleRoute allowed={['reviewer','manager','admin']}`).
 *  - **Insights** (`/insights/*`): viste aggregate e di team per l'analisi HR
 *    (ex "Livelli"); protetta da `LevelsRoute` (admin o `levelsEnabled`).
 *
 * L'area amministrativa resta fuori dalle tre sezioni, sotto `/admin/*`
 * (`RoleRoute allowed={['admin']}`, con `/admin/competences` e `/admin/skills`
 * aperte anche al `manager`). `/login` e `/callback` sono pubbliche (OIDC).
 *
 * Per non rompere bookmark e link esterni al vecchio schema di rotte, i path
 * storici (`/skills`, `/jobs`, `/certifications`, `/stats`, `/team/*`, ...)
 * reindirizzano ai nuovi con `<Navigate replace>`.
 *
 * Le guardie lato client sono DIFENSIVE: l'autorizzazione autorevole resta
 * l'API, che nega qualsiasi accesso non consentito con HTTP 403 a prescindere
 * dal `Role`. Il client tratta il 403 come accesso negato. *Validates: 4.5.*
 */
/**
 * `LegacyRedirect` — reindirizza un vecchio path al corrispondente nuovo,
 * preservando i segmenti dinamici dopo il prefisso `from` e la query string.
 *
 * Esempi (con `from="/team"`, `to="/insights/team"`):
 *  - `/team/123`                  → `/insights/team/123`
 *  - `/team/123/jobs/9`           → `/insights/team/123/jobs/9`
 *  - `/stats?view=skills`         → `/insights?view=skills` (from `/stats`, to `/insights`)
 */
function LegacyRedirect({ from, to }: { from: string; to: string }) {
  const location = useLocation();
  // Segmenti che seguono il prefisso `from` (può essere vuoto per il path esatto).
  const rest = location.pathname.startsWith(from)
    ? location.pathname.slice(from.length)
    : '';
  const target = `${to}${rest}${location.search}${location.hash}`;
  return <Navigate to={target} replace />;
}

function Placeholder({ title }: { title: string }) {
  return (
    <section className="ui-empty" aria-labelledby="notfound-title">
      <h1 id="notfound-title">{title}</h1>
      <p>In costruzione.</p>
    </section>
  );
}

/**
 * `App` — split di primo livello tra la **sezione pubblica** (fuori dallo shell
 * autenticato) e il resto dell'applicazione.
 *
 * Le rotte pubbliche `/public/submission` e `/public/submission/:token` rendono
 * un albero "bare" SENZA `Layout` (niente navbar/chrome autenticato) e fuori
 * dalle guardie di autorizzazione. `AuthProvider` resta montato (in `main.tsx`),
 * ma queste pagine non chiamano mai `login()` né leggono lo `user`.
 *
 * Ogni altra rotta continua a passare per `<Layout>` con le guardie per ruolo
 * (vedi `AppShell`). *Validates: 6.1, 6.3.*
 */
export default function App() {
  return (
    <Routes>
      {/* Sezione pubblica: caricamento e recap CV, resi fuori da `Layout`.
          Le page component sono implementate nei task 11.2/11.3. */}
      <Route
        path="/public/submission"
        element={
          <PublicLayout>
            <PublicCvUpload />
          </PublicLayout>
        }
      />
      <Route
        path="/public/submission/:token"
        element={
          <PublicAppLayout>
            <PublicCvRecap />
          </PublicAppLayout>
        }
      />
      <Route
        path="/public/submission/:token/valuta"
        element={
          <PublicAppLayout>
            <PublicSkillAssessment />
          </PublicAppLayout>
        }
      />
      <Route
        path="/public/submission/:token/competences/:competenceId"
        element={
          <PublicAppLayout>
            <PublicCvCompetenceDetail />
          </PublicAppLayout>
        }
      />
      <Route
        path="/public/submission/:token/jobs/:jobId"
        element={
          <PublicAppLayout>
            <PublicCvJobDetail />
          </PublicAppLayout>
        }
      />
      <Route
        path="/public/submission/:token/certifications/:certificationId"
        element={
          <PublicAppLayout>
            <PublicCvCertificationDetail />
          </PublicAppLayout>
        }
      />

      {/* Tutto il resto passa per lo shell autenticato (`Layout` + guardie). */}
      <Route path="*" element={<AppShell />} />
    </Routes>
  );
}

/**
 * `AppShell` — l'applicazione autenticata dentro `<Layout>`.
 *
 * La navbar (dentro <Layout>) legge il ruolo da `useAuth()` per condizionare i
 * link; qui restano solo le rotte e le guardie. App è dentro <AuthProvider>
 * (main.tsx), quindi `useAuth()` è disponibile ai figli.
 */
function AppShell() {
  return (
    <Layout>
      <Routes>
          {/* Rotte pubbliche: avvio e completamento del flusso OIDC. */}
          <Route path="/login" element={<Login />} />
          <Route path="/callback" element={<Callback />} />
          {/* Changelog: pagina pubblica raggiunta dal link nel footer. */}
          <Route path="/changelog" element={<Changelog />} />

          {/* Sezione Assessment: analisi personale (tutti i ruoli autenticati).
              La radice `/` è la home (hub) di scelta della sezione. */}
          <Route element={<ProtectedRoute />}>
            <Route path="/" element={<Home />} />
            {/* La "dashboard" di Assessment è ora la pagina "Il mio CV", che ne
                è un superset (skill valutate, competenze, professioni, oltre al
                CV Europass). La vecchia Dashboard è stata rimpiazzata. */}
            <Route path="/assessment" element={<MyCv />} />
            <Route path="/assessment/wizard" element={<AssessmentWizard />} />
            <Route path="/assessment/skills" element={<SkillList />} />
            <Route
              path="/assessment/skills/wizard"
              element={<SkillAssessmentWizard />}
            />
            <Route path="/assessment/jobs" element={<JobPositioning />} />
            <Route path="/assessment/jobs/:jobId" element={<JobDetail />} />
            <Route
              path="/assessment/certifications"
              element={<MyCertifications />}
            />
            <Route
              path="/assessment/certifications/:certificationId"
              element={<MyCertificationDetail />}
            />
            <Route
              path="/assessment/competences/:competenceId"
              element={<CompetenceDetail />}
            />
            <Route path="/profile" element={<Profile />} />
            {/* Redirect di compatibilità: la pagina "Il mio CV" vive ora alla
                radice della sezione Assessment (`/assessment`). */}
            <Route path="/me/cv" element={<Navigate to="/assessment" replace />} />
          </Route>

          {/* Sezione Insights (ex "Livelli"): viste aggregate e di team,
              riservata all'admin o agli User con `levelsEnabled`. */}
          <Route element={<LevelsRoute />}>
            <Route path="/insights" element={<CatalogLevels />} />
            <Route
              path="/insights/certifications/:certificationId"
              element={<CertificationDetail />}
            />
            <Route path="/insights/skills/:skillId" element={<SkillDetail />} />
            <Route
              path="/insights/competences/:competenceId"
              element={<CompetenceLevelDetail />}
            />
            <Route path="/insights/team/:userId" element={<TeamMemberDashboard />} />
            <Route
              path="/insights/team/:userId/competences/:competenceId"
              element={<TeamMemberCompetenceDetail />}
            />
            <Route
              path="/insights/team/:userId/certifications/:certificationId"
              element={<TeamMemberCertificationDetail />}
            />
            <Route path="/insights/team/:userId/jobs/:jobId" element={<JobDetail />} />
          </Route>

          {/* Sezione CV esterni: gestione dei profili di candidati esterni,
              riservata all'admin o agli User con `cvEnabled` (permesso
              specifico della sezione CV). */}
          <Route element={<CvRoute />}>
            <Route path="/external-cv" element={<ExternalCvList />} />
            <Route path="/external-cv/:id" element={<ExternalCvDetail />} />
            <Route
              path="/external-cv/:id/competences/:competenceId"
              element={<ExternalCvCompetenceDetail />}
            />
            <Route path="/external-cv/:id/jobs/:jobId" element={<ExternalCvJobDetail />} />
            <Route
              path="/external-cv/:id/certifications/:certificationId"
              element={<ExternalCvCertificationDetail />}
            />
            {/* Sezione Explore: ricerche salvate di talenti. Stessa capability
                della sezione CV (admin o `cvEnabled`): lista + dettaglio con i
                risultati di matching. */}
            <Route path="/explore" element={<ExploreList />} />
            <Route path="/explore/:id" element={<ExploreDetail />} />
          </Route>

          {/* Review: riservata a reviewer, manager e admin (l'admin agisce da manager). */}
          <Route element={<RoleRoute allowed={['reviewer', 'manager', 'admin']} />}>
            <Route path="/review" element={<ReviewPanel />} />
          </Route>

          {/* Redirect di compatibilità dai vecchi path al nuovo schema di rotte.
              Le sotto-rotte usano `:splat` per preservare i segmenti dinamici e
              l'eventuale query string (es. `/stats?view=skills`). */}
          <Route path="/dashboard" element={<Navigate to="/assessment" replace />} />
          <Route
            path="/skills"
            element={<Navigate to="/assessment/skills" replace />}
          />
          <Route path="/jobs/*" element={<LegacyRedirect to="/assessment/jobs" from="/jobs" />} />
          <Route
            path="/certifications/*"
            element={<LegacyRedirect to="/assessment/certifications" from="/certifications" />}
          />
          <Route
            path="/competences/*"
            element={<LegacyRedirect to="/assessment/competences" from="/competences" />}
          />
          <Route path="/stats/*" element={<LegacyRedirect to="/insights" from="/stats" />} />
          <Route
            path="/team/*"
            element={<LegacyRedirect to="/insights/team" from="/team" />}
          />

          {/* Catalogo skill/competenze: accessibile a manager e admin. La UI e
              l'API limitano le singole azioni (il manager può aggiungere skill
              e modificare le competenze del proprio reparto; il resto è admin). */}
          <Route element={<RoleRoute allowed={['manager', 'admin']} />}>
            <Route path="/admin/competences" element={<CompetenceAdmin />} />
            <Route path="/admin/skills" element={<SkillAdmin />} />
          </Route>

          {/* Area admin: riservata al ruolo admin (guardia difensiva). */}
          <Route element={<RoleRoute allowed={['admin']} />}>
            <Route path="/admin" element={<Navigate to="/admin/org" replace />} />
            <Route path="/admin/org" element={<DepartmentAdmin />} />
            <Route path="/admin/users" element={<UserAdmin />} />
            <Route path="/admin/certifications" element={<CertificationAdmin />} />
            <Route path="/admin/jobs" element={<JobAdmin />} />
            <Route path="/admin/scoring" element={<AdminScoringSettings />} />
          </Route>

          <Route path="*" element={<Placeholder title="Pagina non trovata" />} />
      </Routes>
    </Layout>
  );
}
